/**
 * Making real-world ISF compile as GLSL ES 1.00.
 *
 * `IMG_NORM_PIXEL`, `IMG_THIS_PIXEL` and `IMG_THIS_NORM_PIXEL` are single-argument
 * and can be plain `#define`s. `IMG_PIXEL` and `IMG_SIZE` cannot: they need the
 * image's dimensions, which would require pasting the argument into a uniform
 * name — and **GLSL ES 1.00's preprocessor has no `##` token pasting**.
 *
 * VIDOS works around this with a non-standard `IMG_SIZE_<name>` spelling, which
 * means shaders written for VDMX or Resolume — where `IMG_SIZE(inputImage)` is
 * the normal thing to write — simply fail to compile on the device. We instead
 * rewrite at the source level, so unmodified ISF from the wild works, and the
 * same pass should be ported to `ISFParser::PreprocessISFShader`.
 */

import { rewriteCalls, findCalls } from './glsl';
import type { ISFDoc, ISFDiagnostic } from './types';

/** Uniform holding the pixel dimensions of image input `name`. */
export function sizeUniformName(name: string): string {
  return `${name}_size`;
}

/**
 * Every name that can legally be the argument to `IMG_SIZE` / `IMG_PIXEL`:
 * image-ish inputs, imported images, and buffers produced by earlier passes.
 */
export function sampleableNames(doc: ISFDoc): Set<string> {
  const names = new Set<string>();
  for (const input of doc.INPUTS ?? []) {
    if (input.TYPE === 'image' || input.TYPE === 'audio' || input.TYPE === 'audioFFT') {
      names.add(input.NAME);
    }
  }
  for (const name of Object.keys(doc.IMPORTED ?? {})) names.add(name);
  for (const pass of doc.PASSES ?? []) {
    if (pass.TARGET) names.add(pass.TARGET);
  }
  return names;
}

export interface RewriteResult {
  code: string;
  diagnostics: ISFDiagnostic[];
}

/**
 * Rewrite `IMG_SIZE(x)` to `x_size` and `IMG_PIXEL(x, c)` to a normalised
 * `texture2D`. Both rewrites are line-count preserving.
 *
 * An argument that is not a known sampler is reported rather than silently
 * emitting an undeclared identifier, because the resulting GLSL error would
 * point at a name the author never wrote.
 */
export function rewriteImageMacros(body: string, doc: ISFDoc): RewriteResult {
  const known = sampleableNames(doc);
  const diagnostics: ISFDiagnostic[] = [];
  const unknown = new Set<string>();

  const check = (name: string): boolean => {
    if (known.has(name)) return true;
    if (!unknown.has(name)) {
      unknown.add(name);
      diagnostics.push({
        line: 1,
        severity: 'error',
        code: 'ISF_UNKNOWN_IMAGE',
        message: `"${name}" is used as an image but is not an image INPUT, an IMPORTED entry or a PASS TARGET.`,
      });
    }
    return false;
  };

  let code = rewriteCalls(body, 'IMG_SIZE', (args) => {
    if (args.length !== 1) return null;
    const name = args[0];
    if (!check(name)) return null;
    return sizeUniformName(name);
  });

  code = rewriteCalls(code, 'IMG_PIXEL', (args) => {
    if (args.length !== 2) return null;
    const [name, coord] = args;
    if (!check(name)) return null;
    // IMG_PIXEL takes pixel coordinates; texture2D wants normalised ones.
    return `texture2D(${name}, (${coord}) / ${sizeUniformName(name)})`;
  });

  return { code, diagnostics };
}

/** True when the body uses a macro that needs the source-level rewrite. */
export function usesRewrittenMacros(body: string): boolean {
  return findCalls(body, 'IMG_SIZE').length > 0 || findCalls(body, 'IMG_PIXEL').length > 0;
}
